import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_UNIT_ROUTER } from "../../../services/api-config";

// ===== Enums =====

/**
 * Nhóm đo lường của đơn vị.
 *
 * Đây KHÔNG phải nhãn hiển thị — nó quyết định vật tư mang đơn vị đó có vào biên bản kiểm đếm hay
 * không, và quy đổi chỉ có nghĩa trong cùng một nhóm (ml quy về lít được, quy về cái thì không).
 */
export enum MEASURE_TYPE {
  COUNT = 1,
  WEIGHT = 2,
  OCCURRENCE = 3,
}

export const MEASURE_TYPE_NAME: Record<number, string> = {
  [MEASURE_TYPE.COUNT]: "Đếm",
  [MEASURE_TYPE.WEIGHT]: "Khối lượng / thể tích",
  [MEASURE_TYPE.OCCURRENCE]: "Số lần",
};

export const MEASURE_TYPE_HINT: Record<number, string> = {
  [MEASURE_TYPE.COUNT]: "cái, tấm, bộ — đếm được từng món, vào biên bản kiểm đếm",
  [MEASURE_TYPE.WEIGHT]: "kg, gram, lít, ml — cân đo, quy đổi được với nhau",
  [MEASURE_TYPE.OCCURRENCE]: "lần — tính theo lần phát sinh, không phải vật thể",
};

export enum UNIT_STATUS {
  INACTIVE = 0,
  ACTIVE = 1,
}

export const UNIT_STATUS_NAME: Record<number, string> = {
  [UNIT_STATUS.INACTIVE]: "Ngưng sử dụng",
  [UNIT_STATUS.ACTIVE]: "Đang sử dụng",
};

// ===== Interfaces =====

export interface Unit {
  id: number;
  code: string;
  name: string;
  measure_type: number;
  /** 0 = chính nó là đơn vị gốc. */
  base_unit_id: number;
  factor: number;
  sort_order: number;
  status: number;
  created_at?: string;
  updated_at?: string;
}

export interface UnitListFilters {
  measure_type?: number;
  status?: number;
  key_search?: string;
  page?: number;
  limit?: number;
}

export interface CreateUnitPayload {
  code: string;
  name: string;
  measure_type: number;
  base_unit_id?: number;
  factor?: number;
  sort_order?: number;
}

export interface UpdateUnitPayload extends Partial<CreateUnitPayload> {
  status?: number;
}

// ===== Service =====

const UnitService = {
  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<Unit[]>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (params?: UnitListFilters) =>
        request({
          endPoint: API_UNIT_ROUTER.API_LIST,
          data: params || {},
        }),
      response: baseResponse,
      loading,
    };
  },

  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<Unit>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (payload: CreateUnitPayload) =>
        request({
          endPoint: API_UNIT_ROUTER.API_CREATE,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  update: () => {
    const { request, baseResponse, loading } = useFetchData.Post<Unit>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (id: number, payload: UpdateUnitPayload) =>
        request({
          endPoint: API_UNIT_ROUTER.API_UPDATE(id),
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  changeStatus: () => {
    const { request, baseResponse, loading } = useFetchData.Post<Unit>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (id: number, status: number) =>
        request({
          endPoint: API_UNIT_ROUTER.API_CHANGE_STATUS(id),
          data: { status },
        }),
      response: baseResponse,
      loading,
    };
  },
};

export default UnitService;
